import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import { buildAuthorSnapshot } from './authorSnapshot';
import type {
  FeedbackComment,
  FeedbackPlatform,
  FeedbackPost,
  FeedbackPostStatus,
  FeedbackPostType,
} from './types';
import {
  FEEDBACK_COMMENTS_SUBCOLLECTION,
  FEEDBACK_POSTS_COLLECTION,
} from './types';

function mapPost(id: string, data: Record<string, unknown>): FeedbackPost {
  return {
    id,
    authorId: String(data.authorId ?? ''),
    authorUsername: String(data.authorUsername ?? ''),
    authorDisplayName: String(data.authorDisplayName ?? ''),
    authorPhotoURL:
      typeof data.authorPhotoURL === 'string' ? data.authorPhotoURL : null,
    role: 'creator',
    feedbackType: (data.feedbackType as FeedbackPostType) || 'general',
    platform: (data.platform as FeedbackPlatform) || 'other',
    title: String(data.title ?? ''),
    description: String(data.description ?? ''),
    contentUrl:
      typeof data.contentUrl === 'string' && data.contentUrl
        ? data.contentUrl
        : null,
    feedbackPrompt:
      typeof data.feedbackPrompt === 'string' && data.feedbackPrompt.trim()
        ? data.feedbackPrompt.trim()
        : null,
    status: (data.status as FeedbackPostStatus) === 'closed' ? 'closed' : 'open',
    commentCount: typeof data.commentCount === 'number' ? data.commentCount : 0,
    participantCount:
      typeof data.participantCount === 'number' ? data.participantCount : 1,
    participantUserIds: Array.isArray(data.participantUserIds)
      ? (data.participantUserIds as string[])
      : [],
    lastActivityAt: (data.lastActivityAt as Timestamp) || null,
    createdAt: (data.createdAt as Timestamp) || null,
    updatedAt: (data.updatedAt as Timestamp) || null,
    tags: Array.isArray(data.tags) ? (data.tags as string[]) : [],
    solvedCommentId:
      typeof data.solvedCommentId === 'string' ? data.solvedCommentId : null,
    isEdited: Boolean(data.isEdited),
  };
}

function mapComment(id: string, data: Record<string, unknown>): FeedbackComment {
  return {
    id,
    authorId: String(data.authorId ?? ''),
    authorUsername: String(data.authorUsername ?? ''),
    authorDisplayName: String(data.authorDisplayName ?? ''),
    authorPhotoURL:
      typeof data.authorPhotoURL === 'string' ? data.authorPhotoURL : null,
    body: String(data.body ?? ''),
    createdAt: (data.createdAt as Timestamp) || null,
    updatedAt: (data.updatedAt as Timestamp) || null,
    isEdited: Boolean(data.isEdited),
  };
}

export async function createFeedbackPost(params: {
  authorId: string;
  feedbackType: FeedbackPostType;
  platform: FeedbackPlatform;
  title: string;
  description: string;
  contentUrl: string | null;
  feedbackPrompt: string | null;
}): Promise<string> {
  const author = await buildAuthorSnapshot(params.authorId);
  const postsRef = collection(db, FEEDBACK_POSTS_COLLECTION);
  const now = serverTimestamp();
  const docRef = await addDoc(postsRef, {
    authorId: author.authorId,
    authorUsername: author.authorUsername,
    authorDisplayName: author.authorDisplayName,
    authorPhotoURL: author.authorPhotoURL,
    role: 'creator',
    feedbackType: params.feedbackType,
    platform: params.platform,
    title: params.title.trim(),
    description: params.description.trim(),
    contentUrl: params.contentUrl?.trim() || null,
    feedbackPrompt: params.feedbackPrompt?.trim() || null,
    status: 'open',
    commentCount: 0,
    participantCount: 1,
    participantUserIds: [params.authorId],
    lastActivityAt: now,
    createdAt: now,
    updatedAt: now,
    tags: [],
    solvedCommentId: null,
    isEdited: false,
  });
  return docRef.id;
}

export async function getFeedbackPost(postId: string): Promise<FeedbackPost | null> {
  const ref = doc(db, FEEDBACK_POSTS_COLLECTION, postId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return mapPost(snap.id, snap.data() as Record<string, unknown>);
}

export function subscribeFeedbackPosts(
  maxPosts: number,
  onData: (posts: FeedbackPost[]) => void,
  onError: (e: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, FEEDBACK_POSTS_COLLECTION),
    orderBy('lastActivityAt', 'desc'),
    limit(maxPosts)
  );
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => mapPost(d.id, d.data() as Record<string, unknown>)));
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err)))
  );
}

export function subscribeFeedbackPost(
  postId: string,
  onData: (post: FeedbackPost | null) => void,
  onError: (e: Error) => void
): Unsubscribe {
  const ref = doc(db, FEEDBACK_POSTS_COLLECTION, postId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) onData(null);
      else onData(mapPost(snap.id, snap.data() as Record<string, unknown>));
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err)))
  );
}

export function subscribeFeedbackComments(
  postId: string,
  onData: (comments: FeedbackComment[]) => void,
  onError: (e: Error) => void
): Unsubscribe {
  const q = query(
    collection(
      db,
      FEEDBACK_POSTS_COLLECTION,
      postId,
      FEEDBACK_COMMENTS_SUBCOLLECTION
    ),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(
    q,
    (snap) => {
      onData(
        snap.docs.map((d) => mapComment(d.id, d.data() as Record<string, unknown>))
      );
    },
    (err) => onError(err instanceof Error ? err : new Error(String(err)))
  );
}

export type AddFeedbackCommentResult = {
  commentId: string;
  postAuthorId: string;
  postTitle: string;
};

export async function addFeedbackComment(params: {
  postId: string;
  authorId: string;
  body: string;
}): Promise<AddFeedbackCommentResult> {
  const author = await buildAuthorSnapshot(params.authorId);
  const postRef = doc(db, FEEDBACK_POSTS_COLLECTION, params.postId);
  const commentsCol = collection(
    db,
    FEEDBACK_POSTS_COLLECTION,
    params.postId,
    FEEDBACK_COMMENTS_SUBCOLLECTION
  );

  let commentId: string;
  let postAuthorId: string;
  let postTitle: string;

  await runTransaction(db, async (transaction) => {
    const postSnap = await transaction.get(postRef);
    if (!postSnap.exists()) {
      throw new Error('Post not found.');
    }
    const postData = postSnap.data() as Record<string, unknown>;
    if (postData.status === 'closed') {
      throw new Error('This post is closed to new comments.');
    }
    postAuthorId = String(postData.authorId ?? '');
    postTitle = String(postData.title ?? '');
    const participantUserIds = Array.isArray(postData.participantUserIds)
      ? (postData.participantUserIds as string[])
      : [];
    const isNewParticipant = !participantUserIds.includes(params.authorId);

    const commentRef = doc(commentsCol);
    commentId = commentRef.id;
    transaction.set(commentRef, {
      authorId: author.authorId,
      authorUsername: author.authorUsername,
      authorDisplayName: author.authorDisplayName,
      authorPhotoURL: author.authorPhotoURL,
      body: params.body,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isEdited: false,
    });

    const updates: Record<string, unknown> = {
      commentCount: increment(1),
      lastActivityAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (isNewParticipant) {
      updates.participantUserIds = [...participantUserIds, params.authorId];
      updates.participantCount = increment(1);
    }

    transaction.update(postRef, updates);
  });

  return { commentId, postAuthorId, postTitle };
}

/**
 * Delete a comment on a feedback post. Only the comment author may delete.
 * Decrements parent post commentCount and refreshes lastActivityAt / updatedAt.
 * participantCount and participantUserIds are unchanged (see tradeoff in docs).
 */
export async function deleteFeedbackComment(params: {
  postId: string;
  commentId: string;
  currentUserId: string;
}): Promise<void> {
  const postRef = doc(db, FEEDBACK_POSTS_COLLECTION, params.postId);
  const commentRef = doc(
    db,
    FEEDBACK_POSTS_COLLECTION,
    params.postId,
    FEEDBACK_COMMENTS_SUBCOLLECTION,
    params.commentId
  );

  await runTransaction(db, async (transaction) => {
    const commentSnap = await transaction.get(commentRef);
    if (!commentSnap.exists()) {
      throw new Error('Comment not found.');
    }
    const cData = commentSnap.data() as Record<string, unknown>;
    if (String(cData.authorId ?? '') !== params.currentUserId) {
      throw new Error('Not allowed.');
    }

    const postSnap = await transaction.get(postRef);
    if (!postSnap.exists()) {
      throw new Error('Post not found.');
    }
    const pData = postSnap.data() as Record<string, unknown>;
    const count = typeof pData.commentCount === 'number' ? pData.commentCount : 0;
    if (count < 1) {
      throw new Error('Invalid comment count.');
    }

    transaction.delete(commentRef);
    // Use explicit count so security rules can evaluate:
    // request.resource.data.commentCount == resource.data.commentCount - 1
    // (increment(-1) is not reliably comparable in rules.)
    transaction.update(postRef, {
      commentCount: count - 1,
      lastActivityAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
}

export async function updateFeedbackPostStatus(
  postId: string,
  authorId: string,
  status: FeedbackPostStatus
): Promise<void> {
  const post = await getFeedbackPost(postId);
  if (!post || post.authorId !== authorId) {
    throw new Error('Not allowed.');
  }
  await updateDoc(doc(db, FEEDBACK_POSTS_COLLECTION, postId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

export type UpdateFeedbackPostData = {
  title: string;
  description: string;
  feedbackType: FeedbackPostType;
  platform: FeedbackPlatform;
  contentUrl: string | null;
  feedbackPrompt: string | null;
  status?: FeedbackPostStatus;
};

export async function updateFeedbackPost(
  postId: string,
  authorId: string,
  data: UpdateFeedbackPostData
): Promise<void> {
  const post = await getFeedbackPost(postId);
  if (!post || post.authorId !== authorId) {
    throw new Error('Not allowed.');
  }
  await updateDoc(doc(db, FEEDBACK_POSTS_COLLECTION, postId), {
    title: data.title.trim(),
    description: data.description.trim(),
    feedbackType: data.feedbackType,
    platform: data.platform,
    contentUrl: data.contentUrl?.trim() || null,
    feedbackPrompt: data.feedbackPrompt?.trim() || null,
    ...(data.status != null && { status: data.status }),
    updatedAt: serverTimestamp(),
    isEdited: true,
  });
}

/**
 * Delete a feedback post and its comments subcollection.
 * Only the post author can delete. Comments are deleted first (post author can delete via rules).
 */
export async function deleteFeedbackPost(
  postId: string,
  authorId: string
): Promise<void> {
  const post = await getFeedbackPost(postId);
  if (!post || post.authorId !== authorId) {
    throw new Error('Not allowed.');
  }
  const commentsRef = collection(
    db,
    FEEDBACK_POSTS_COLLECTION,
    postId,
    FEEDBACK_COMMENTS_SUBCOLLECTION
  );
  const commentsSnap = await getDocs(commentsRef);
  for (const d of commentsSnap.docs) {
    await deleteDoc(d.ref);
  }
  await deleteDoc(doc(db, FEEDBACK_POSTS_COLLECTION, postId));
}
